import { useState, useEffect } from 'react';
import { PageHeader, Badge, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface AlertRule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  severity: 'critical' | 'high' | 'medium' | 'low';
  type: 'threshold' | 'anomaly' | 'pattern' | 'correlation';
  source: string;
  conditions: string[];
  actions: string[];
  created_at: string;
  updated_at: string;
  triggered_count: number;
  last_triggered?: string;
}

const mockRules: AlertRule[] = [
  {
    id: 'rule-001',
    name: 'Brute Force Login Detection',
    description: 'Triggers when multiple failed login attempts are detected from the same IP address',
    enabled: true,
    severity: 'critical',
    type: 'threshold',
    source: 'Azure AD',
    conditions: ['Failed login attempts > 10 in 5 minutes', 'Same source IP'],
    actions: ['Send email alert', 'Block IP address', 'Create security incident'],
    created_at: '2026-04-15T10:00:00Z',
    updated_at: '2026-05-20T14:30:00Z',
    triggered_count: 23,
    last_triggered: '2026-05-22T10:45:00Z'
  },
  {
    id: 'rule-002',
    name: 'Large Data Transfer Detection',
    description: 'Alerts on unusual large data transfers from production systems',
    enabled: true,
    severity: 'high',
    type: 'anomaly',
    source: 'AWS CloudTrail',
    conditions: ['Data transfer > 1GB', 'Outside business hours', 'Production environment'],
    actions: ['Send Slack notification', 'Create alert ticket'],
    created_at: '2026-04-10T09:15:00Z',
    updated_at: '2026-05-18T11:00:00Z',
    triggered_count: 7,
    last_triggered: '2026-05-22T09:30:00Z'
  },
  {
    id: 'rule-003',
    name: 'Malware Detection',
    description: 'Immediate alert when malware is detected on any endpoint',
    enabled: true,
    severity: 'critical',
    type: 'pattern',
    source: 'CrowdStrike',
    conditions: ['Malware signature match', 'Real-time scanning'],
    actions: ['Immediate SMS alert', 'Isolate endpoint', 'Create P0 incident'],
    created_at: '2026-03-22T16:45:00Z',
    updated_at: '2026-05-15T08:20:00Z',
    triggered_count: 12,
    last_triggered: '2026-05-22T08:15:00Z'
  },
  {
    id: 'rule-004',
    name: 'Privilege Escalation Detection',
    description: 'Detects unauthorized attempts to gain elevated privileges',
    enabled: true,
    severity: 'high',
    type: 'correlation',
    source: 'Application Logs',
    conditions: ['Admin endpoint access', 'Non-admin user', 'Multiple attempts'],
    actions: ['Email security team', 'Temporary account suspension'],
    created_at: '2026-04-05T13:30:00Z',
    updated_at: '2026-05-19T16:10:00Z',
    triggered_count: 18,
    last_triggered: '2026-05-22T07:00:00Z'
  },
  {
    id: 'rule-005',
    name: 'Suspicious Network Activity',
    description: 'Alerts on connections to known malicious domains or IPs',
    enabled: false,
    severity: 'medium',
    type: 'pattern',
    source: 'Network IDS',
    conditions: ['Known C2 server communication', 'Outbound connection'],
    actions: ['Log event', 'Email weekly summary'],
    created_at: '2026-03-18T11:00:00Z',
    updated_at: '2026-05-10T09:45:00Z',
    triggered_count: 45,
    last_triggered: '2026-05-22T06:30:00Z'
  }
];

const severityTone: Record<string, ToneType> = {
  critical: 'crit',
  high: 'warn',
  medium: 'info',
  low: 'muted'
};

const typeBadgeColor: Record<string, ToneType> = {
  threshold: 'info',
  anomaly: 'warn',
  pattern: 'draft',
  correlation: 'ok'
};

function formatTimeAgo(timestamp: string): string {
  const now = new Date();
  const eventTime = new Date(timestamp);
  const diffMs = now.getTime() - eventTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default function AlertRules() {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const loadRules = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setRules(mockRules);
      setLoading(false);
    };
    
    loadRules();
  }, []);

  const filteredRules = rules.filter(rule => {
    const matchesFilter = filter === 'all' || 
      (filter === 'enabled' && rule.enabled) ||
      (filter === 'disabled' && !rule.enabled) ||
      rule.severity === filter ||
      rule.type === filter;
    
    const matchesSearch = search === '' || 
      rule.name.toLowerCase().includes(search.toLowerCase()) ||
      rule.description.toLowerCase().includes(search.toLowerCase()) ||
      rule.source.toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const ruleStats = {
    total: rules.length,
    enabled: rules.filter(r => r.enabled).length,
    critical: rules.filter(r => r.severity === 'critical').length,
    triggered_today: rules.filter(r => {
      if (!r.last_triggered) return false;
      const triggerDate = new Date(r.last_triggered);
      const today = new Date();
      return triggerDate.toDateString() === today.toDateString();
    }).length
  };

  const handleToggleRule = (ruleId: string) => {
    setRules(prev => prev.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading alert rules...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Alert Rules"
        subtitle="Configure rules for automated alert generation based on security events and patterns."
        right={
          <div className="flex items-center gap-3">
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export Rules
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="plus" size={14} stroke="white" /> Create Rule
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Rules</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{ruleStats.total}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Enabled</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#159D4C' }}>{ruleStats.enabled}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Critical Rules</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{ruleStats.critical}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Triggered Today</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#CA9401' }}>{ruleStats.triggered_today}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search rules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="all">All Rules</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
            <option value="critical">Critical</option>
            <option value="high">High Priority</option>
            <option value="threshold">Threshold</option>
            <option value="anomaly">Anomaly</option>
            <option value="pattern">Pattern</option>
          </select>
        </div>
      </div>

      {/* Rules List */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Alert Rules ({filteredRules.length})
        </div>
        
        <ul>
          {filteredRules.map((rule) => (
            <li 
              key={rule.id} 
              className="px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-display font-bold text-ink text-[16px]">{rule.name}</h3>
                    <Badge tone={severityTone[rule.severity]} dot className="capitalize">
                      {rule.severity}
                    </Badge>
                    <Badge tone={typeBadgeColor[rule.type]} className="capitalize">
                      {rule.type}
                    </Badge>
                    <Badge tone={rule.enabled ? 'ok' : 'muted'}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  
                  <p className="font-display text-[14px] text-ink/65 mb-3 line-clamp-2">
                    {rule.description}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-[12px] text-ink/50 mb-3">
                    <div>
                      <strong>Source:</strong> {rule.source}
                    </div>
                    <div>
                      <strong>Triggered:</strong> {rule.triggered_count} times
                    </div>
                    {rule.last_triggered && (
                      <div>
                        <strong>Last triggered:</strong> {formatTimeAgo(rule.last_triggered)}
                      </div>
                    )}
                    <div>
                      <strong>Updated:</strong> {formatTimeAgo(rule.updated_at)}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <span className="font-display font-semibold text-[12px] text-ink/70">Conditions:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule.conditions.map((condition, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#DDF4FF] text-[#0A69DA] font-display text-[10px] font-semibold">
                            {condition}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <span className="font-display font-semibold text-[12px] text-ink/70">Actions:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule.actions.map((action, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#C6F3D8] text-[#159D4C] font-display text-[10px] font-semibold">
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleToggleRule(rule.id)}
                    className={`h-8 px-3 rounded-[6px] font-display font-bold text-[12px] transition ${
                      rule.enabled 
                        ? 'bg-[#CA9401] text-white hover:opacity-90' 
                        : 'bg-[#159D4C] text-white hover:opacity-90'
                    }`}
                  >
                    {rule.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button className="h-8 px-3 rounded-[6px] border border-[#F3E4C8] bg-white font-display font-bold text-[12px] text-ink hover:border-ink/40">
                    Edit
                  </button>
                  <button className="h-8 w-8 rounded-[6px] border border-[#F3E4C8] bg-white grid place-items-center hover:border-ink/40">
                    <Icon name="more-horizontal" size={14} stroke="#121111" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}